import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getWeeklyTrafficData } from '../../redux/actions/weeklyTrafficActions';
import { Bar } from 'react-chartjs-2';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import classes from './WeeklyNettraffic.module.css';
import IPTables from './IPTables';
import { useTheme } from '../../contexts/ThemeContext';
import NetworkError from '../../components/Error';


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const FRONTEND_WEEKLY_TRAFFIC_ERROR_CODES = {
  DATA_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildWeeklyTrafficSupportError = (errorCode, pageInfo, sourceMessage) =>
  formatFrontendSupportError({
    errorCode,
    pageInfo,
    sourceMessage,
  });

const WeeklyNetTraffic = ({ pageContext = 'Weekly Network Traffic Page' }) => {
  const dispatch = useDispatch();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState(null);
  const [internalIPs, setInternalIPs] = useState([]);
  const [externalIPs, setExternalIPs] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [selectedWatcher, setSelectedWatcher] = useState('');
  const [hasError, setHasError] = useState(false);
  const [supportErrorMessage, setSupportErrorMessage] = useState('');
  const [copiedSupportError, setCopiedSupportError] = useState(false);

  const setWeeklyTrafficSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
    setHasError(true);
    setSupportErrorMessage(buildWeeklyTrafficSupportError(errorCode, pageInfo, sourceMessage));
    setCopiedSupportError(false);
  }, []);

  const clearWeeklyTrafficSupportError = React.useCallback(() => {
    setHasError(false);
    setSupportErrorMessage('');
    setCopiedSupportError(false);
  }, []);

  const handleCopySupportError = React.useCallback(async () => {
    if (!supportErrorMessage) return;
    try {
      await navigator.clipboard.writeText(supportErrorMessage);
      setCopiedSupportError(true);
      setTimeout(() => setCopiedSupportError(false), 2000);
    } catch (error) {
      setCopiedSupportError(false);
    }
  }, [supportErrorMessage]);

  // Generate empty chart data for initial load
  const getEmptyChartData = () => {
    const emptyLabels = Array.from({ length: 7 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (6 - i));
      return date.toISOString().split('T')[0];
    });

    return {
      labels: emptyLabels,
      datasets: [
        {
          label: 'Internal Connections',
          data: new Array(7).fill(0),
          backgroundColor: 'rgba(78, 115, 223, 0.3)', // More transparent when loading
          borderColor: '#4e73df',
          borderWidth: 1,
        },
        {
          label: 'External Connections',
          data: new Array(7).fill(0),
          backgroundColor: 'rgba(231, 74, 59, 0.3)', // More transparent when loading
          borderColor: '#e74a3b',
          borderWidth: 1,
        },
      ],
    };
  };

  // Watcher dropdown options, loaded once on mount. An empty watcher makes the
  // backend return just the list (no net_traffic scan), so the selector
  // populates while the chart stays empty until the user picks a watcher.
  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await dispatch(getWeeklyTrafficData(''));
      if (alive && data && Array.isArray(data.watchers)) setWatchers(data.watchers);
    })();
    return () => { alive = false; };
  }, [dispatch]);

  useEffect(() => {
    const fetchData = async (watcherId) => {
      if (!watcherId) { setLoading(false); return; }   // nothing selected yet — render with no data
      setLoading(true);
      try {
        const response = await dispatch(getWeeklyTrafficData(watcherId));
        if (response && response.status) {
          setWeeklyData(response.data);
          if (response.watchers) setWatchers(response.watchers);
          if (response.internal_ips) setInternalIPs(response.internal_ips);
          if (response.external_ips) setExternalIPs(response.external_ips);
          clearWeeklyTrafficSupportError();
        } else {
          setWeeklyTrafficSupportError(
            FRONTEND_WEEKLY_TRAFFIC_ERROR_CODES.DATA_LOAD_FAILED,
            `${pageContext} / Weekly Traffic Load Failed`,
            response?.msg || 'Weekly traffic request did not return success'
          );
        }
      } catch (error) {
        setWeeklyTrafficSupportError(
          FRONTEND_WEEKLY_TRAFFIC_ERROR_CODES.DATA_LOAD_FAILED,
          `${pageContext} / Weekly Traffic Request Error`,
          error?.message || String(error)
        );
      }
      setLoading(false);
    };

    fetchData(selectedWatcher);

    const interval = setInterval(() => fetchData(selectedWatcher), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [dispatch, selectedWatcher, clearWeeklyTrafficSupportError, setWeeklyTrafficSupportError, pageContext]);

  const handleWatcherChange = (e) => {
    setSelectedWatcher(e.target.value);
  };

  // Determine which chart data to use
  const getChartData = () => {
    if (loading || !weeklyData || weeklyData.length === 0) {
      return getEmptyChartData();
    }

    const labels = weeklyData.map(item => item.date);
    
    return {
      labels,
      datasets: [
        {
          label: 'Internal Connections',
          data: weeklyData.map(item => item.internal),
          backgroundColor: 'rgba(78, 115, 223, 0.8)',
          borderColor: '#4e73df',
          borderWidth: 1,
        },
        {
          label: 'External Connections',
          data: weeklyData.map(item => item.external),
          backgroundColor: 'rgba(231, 74, 59, 0.8)',
          borderColor: '#e74a3b',
          borderWidth: 1,
        },
      ],
    };
  };

  const chartData = getChartData();

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: isDarkMode ? '#ffffff' : '#333333',
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: isDarkMode ? '#333333' : '#ffffff',
        titleColor: isDarkMode ? '#ffffff' : '#333333',
        bodyColor: isDarkMode ? '#ffffff' : '#333333',
        borderColor: isDarkMode ? '#555555' : '#cccccc',
        borderWidth: 1,
        callbacks: {
          footer: (tooltipItems) => {
            if (loading || !weeklyData || weeklyData.length === 0) {
              return 'Loading data...';
            }
            const dataIndex = tooltipItems[0].dataIndex;
            const totalConnections = weeklyData[dataIndex].total;
            return `Total Connections: ${totalConnections}`;
          }
        }
      },
      datalabels: {
        display: !loading, // Hide data labels when loading
        anchor: 'center',
        align: 'center',
        color: isDarkMode ? '#ffffff' : '#333333',
        font: {
          size: 12,
          weight: 'bold',
          family: "'Roboto', sans-serif"
        },
        formatter: (value) => {
          return value.toLocaleString();
        }
      },
    },
    scales: {
      x: {
        stacked: false,
        ticks: {
          color: isDarkMode ? '#ffffff' : '#666666',
        },
        grid: {
          color: isDarkMode ? '#444444' : '#e0e0e0',
        },
      },
      y: {
        stacked: false,
        beginAtZero: true,
        ticks: {
          precision: 0,
          color: isDarkMode ? '#ffffff' : '#666666',
        },
        grid: {
          color: isDarkMode ? '#444444' : '#e0e0e0',
        },
      },
    },
    // Add animation for smooth transition when data loads
    animation: {
      duration: loading ? 0 : 1000, // No animation when loading, smooth when data comes in
      easing: 'easeInOutQuart'
    }
  };

  const hasNoWeeklyData = !weeklyData || weeklyData.length === 0;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h2 className={classes.title}>
          Weekly Network Traffic
          {loading && <span className={classes.loadingIndicator}> (Loading...)</span>}
        </h2>
        <div className={classes.watcherSelectContainer}>
          <select 
            value={selectedWatcher} 
            onChange={handleWatcherChange}
            className={classes.watcherSelect}
            disabled={loading}
          >
            <option value="">Select a Watcher</option>
            <option value="all">All Watchers</option>
            {watchers && watchers.map(watcher => (
              <option key={watcher[0]} value={watcher[0]}>
                {watcher[1]} ({watcher[0]})
              </option>
            ))}
          </select>
        </div>
      </div>

      {hasError && supportErrorMessage && (
        <div className={classes.supportErrorBanner}>
          <div className={classes.supportErrorBannerText}>
            <strong>Support Error:</strong>
            <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
          </div>
          <button
            type='button'
            className={classes.copySupportErrorButton}
            onClick={handleCopySupportError}
          >
            {copiedSupportError ? 'Copied' : 'Copy Error Details'}
          </button>
        </div>
      )}

      {hasError && !loading && hasNoWeeklyData ? (
        <div>
          <NetworkError />
          {supportErrorMessage && (
            <div className={classes.supportErrorCard}>
              <strong>Support Error:</strong>
              <code className={classes.supportErrorCode}>{supportErrorMessage}</code>
              <button
                type='button'
                className={classes.copySupportErrorButton}
                onClick={handleCopySupportError}
              >
                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
      
          <div className={`${classes.chartContainer} ${loading ? classes.loading : ''}`}>
            <Bar data={chartData} options={options} />
            {loading && (
              <div className={classes.loadingOverlay}>
                <div className={classes.loadingSpinner}></div>
              </div>
            )}
          </div>
          
          {/* IP Address Tables */}
          <IPTables 
            internalIPs={internalIPs} 
            externalIPs={externalIPs} 
            loading={loading} 
          />
        </>
      )}
    </div>
  );
};

export default WeeklyNetTraffic;
